<template>
  <fieldset class="mb-0 pb-0">
    <legend v-if="title">
      <h5>
        <strong>{{ title }}</strong>
      </h5>
    </legend>
    <div
      class="form-group"
      :class="[
        {
          'label-floating': label,
          'is-empty': !success && !error && !content,
          'has-success': success && !error,
          'has-error': !success && error,
          'has-disabled': disabled,
          'with-icon': icon || iconSvg,
        },
        otherClasses,
      ]"
    >
      <label v-if="label" class="control-label"
        >{{ label }} <span class="text-danger" v-if="asterisk">*</span>
      </label>
      <input
        v-currency="currencyOptions"
        class="form-control"
        v-model="content"
        :placeholder="placeholder"
        :class="[getSize]"
        :required="required"
        v-on:input="$emit('change', $event.target.value)"
        v-on:keyup.enter="$emit('submit')"
        :data-cy="dataCy"
        @focus="handleFocus"
        @blur="handleBlur"
        :disabled="disabled"
        ref="input"
        :name="name"
        autocomplete="off"
        :id="dynamicID()"
        type="text"
      />
      <md-icon v-if="icon">{{ icon }}</md-icon>
      <md-icon v-if="iconSvg" :md-src="iconSvg" />
      <slot></slot>
    </div>
  </fieldset>
</template>

<script>
// @ is an alias to /src
import { CurrencyDirective } from "vue-currency-input";

export default {
  name: "SInputCurrency",
  props: {
    label: String,
    success: null,
    error: null,
    disabled: Boolean,
    icon: String,
    iconSvg: String,
    title: String,
    required: Boolean,
    input: null,
    dataCy: String,
    size: String,
    otherClasses: String,
    asterisk: { type: Boolean, default: false },
    name: String,
    currencyCode: { type: String, default: null },
    currencySymbol: {
      type: Object,
      default: function (prefix, sufix) {
        return { prefix: prefix, sufix: sufix };
      },
    },
    locale: { type: String, default: undefined },
    autoDecimalMode: { type: Boolean, default: false },
    precisionValue: { type: String, default: undefined },
    precisionRange: {
      type: Object,
      default: function (min, max) {
        return { min: min, max: max };
      },
    },
    isDistractionFree: { type: Boolean, default: true },
    distractionFreeOptions: {
      type: Object,
      default: function (
        hideNegligibleDecimalDigits,
        hideCurrencySymbol,
        hideGroupingSymbol
      ) {
        return {
          hideNegligibleDecimalDigits: hideNegligibleDecimalDigits,
          hideCurrencySymbol: hideCurrencySymbol,
          hideGroupingSymbol: hideGroupingSymbol,
        };
      },
    },
    valueAsInteger: { type: Boolean, default: false },
    valueRangeOptions: {
      type: Object,
      default: function (min, max) {
        return { min: min, max: max };
      },
    },
    allowNegative: { type: Boolean, default: true },
    paymentType: String,
  },
  directives: {
    currency: CurrencyDirective,
  },
  data() {
    return {
      content: this.input,
      inputType: this.type,
      placeholder: "0",
    };
  },
  model: {
    prop: "input",
    event: "change",
  },
  computed: {
    getSize() {
      switch (this.size) {
        case "sm":
          return "form-control-sm";
        case "md-2":
          return "form-control-md-2";
        case "md":
          return "form-control-md";
        case "lg":
          return "form-control-lg";
        default:
          return "";
      }
    },
    currency() {
      if (this.currencyCode) return this.currencyCode;
      return null;
    },
    precision() {
      if (!this.precisionValue) return this.precisionRange;
      return parseInt(this.precisionValue);
    },
    distractionFree() {
      if (!this.isDistractionFree) {
        if (
          this.distractionFreeOptions.hideNegligibleDecimalDigits ||
          this.distractionFreeOptions.hideCurrencySymbol ||
          this.distractionFreeOptions.hideGroupingSymbol
        ) {
          return this.distractionFreeOptions;
        }
      }
      return this.isDistractionFree;
    },
    valueRange() {
      if (this.valueRangeOptions.min && this.valueRangeOptions.max) {
        return this.valueRangeOptions;
      }
      return undefined;
    },
    currencyOptions() {
      return {
        currency: this.currency,
        locale: this.locale,
        autoDecimalMode: this.autoDecimalMode,
        precision: this.precision,
        distractionFree: this.distractionFree,
        valueAsInteger: this.valueAsInteger,
        valueRange: this.valueRange,
        allowNegative: this.allowNegative,
      };
    },
  },
  methods: {
    focus() {
      this.$nextTick(() => {
        const inputRef = this.$refs.input;
        inputRef.focus();
      });
    },
    handleBlur(){
      this.$emit('atBlur')
    },
    handleFocus(){
      this.$emit('atFocus');
      if(this.paymentType === "points")
        this.content = localStorage.getItem('points');
      if(this.paymentType === "cash")
        this.content = localStorage.getItem('cash');
    },
    dynamicID() {
      return "dynamicID-" + Math.floor(Math.random() * Date.now().toString());
    },
  },
  mounted() {
    this.$refs.input.blur();
  },
  watch: {
    disabled(isDisable) {
      if (isDisable) {
        this.content = "";
      }else {
      if(this.paymentType === "points")
        this.content = localStorage.getItem('points');
      if(this.paymentType === "cash")
        this.content = localStorage.getItem('cash');
      }
    }
  },
};
</script>
<style lang="scss" scoped>
fieldset {
  display: flex;
  justify-content: center;
}
input {
  border: 1px solid #000000;
  box-sizing: border-box;
  border-radius: 4px;
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 1px;
  color: #000000;
}
input:focus {
  border: 1px solid $primary !important;
}
.form-group.with-icon i {
  width: 60px;
  display: flex;
  color: $black;
  margin-top: -13px;
}

.form-group.with-icon input{
  padding-left: 50px;
}
</style>
